import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Download, MessageCircle, FileText, RefreshCw,
  CheckCircle2, XCircle, Loader2,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, API, getErr, waToCustomer } from "../../lib/api";

const STATUSES = ["paid", "refunded", "cancelled", "draft"];
const STATUS_COLOR = {
  paid: "bg-emerald-100 text-emerald-700",
  refunded: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
  draft: "bg-gray-100 text-gray-700",
};

export default function AdminInvoiceDetail() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/invoices/${invoiceId}`);
      setInvoice(data);
    } catch (e) { toast.error(getErr(e, "Could not load invoice")); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [invoiceId]);

  if (!invoice) {
    return <div className="p-10 grid place-items-center text-[#5C6661]"><Loader2 className="animate-spin" /></div>;
  }

  const onStatusChange = async (newStatus) => {
    if (!window.confirm(`Change invoice status to ${newStatus.toUpperCase()}?`)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/invoices/${invoiceId}/status`, { status: newStatus });
      toast.success(`Invoice marked ${newStatus}`);
      load();
    } catch (e) { toast.error(getErr(e, "Status update failed")); }
    finally { setBusy(false); }
  };

  const onWaShare = async () => {
    const msg = [
      `Hello ${invoice.customer_name || ""},`, "",
      "Your payment has been successfully received.", "",
      "✅ GST Tax Invoice Generated",
      `📄 Invoice No: ${invoice.invoice_number}`,
      `💰 Amount: ₹${(invoice.total_amount || 0).toLocaleString("en-IN")}`,
      "🧾 GST Included", "",
      `Download: ${API}/admin/invoices/${invoice.id}/pdf`, "",
      "– Team Finsahayak.com",
    ].join("\n");
    const wa = waToCustomer(invoice.customer_mobile, msg);
    window.open(wa, "_blank", "noopener,noreferrer");
    try {
      await api.post(`/admin/invoices/${invoice.id}/whatsapp-log`, {
        channel: "wa.me", template: "tax_invoice_share", message: msg.slice(0, 280),
      });
      toast.success("WhatsApp share logged");
    } catch { /* best-effort */ }
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-5xl mx-auto" data-testid="invoice-detail-page">
      <Toaster richColors position="top-right" />
      <Link to="/admin/invoices" className="inline-flex items-center gap-1.5 text-sm text-[#5C6661] hover:text-[#1E4D3B] mb-3">
        <ArrowLeft size={14} /> Back to invoices
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <span className="label-eyebrow">Tax Invoice</span>
          <h1 className="mt-2 text-3xl font-display font-medium text-[#121C17] font-mono" data-testid="invoice-number">
            {invoice.invoice_number}
          </h1>
          <p className="text-sm text-[#5C6661] mt-1">
            FY {invoice.financial_year} · Issued {(invoice.invoice_date || "").slice(0, 10)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${API}/admin/invoices/${invoice.id}/pdf`} target="_blank" rel="noopener noreferrer"
            data-testid="invoice-download-pdf"
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2">
            <Download size={14} /> Download PDF
          </a>
          <button onClick={onWaShare} data-testid="invoice-share-wa"
            className="rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 text-white"
            style={{ background: "#25D366" }}>
            <MessageCircle size={14} /> Share on WhatsApp
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <Block title="Supplier (Bill from)" testid="block-supplier">
          <KV k="Legal name" v="FinSahayak" />
          <KV k="GSTIN" v={invoice.supplier_gstin} mono />
          <KV k="State" v={`Odisha (${invoice.supplier_state_code})`} />
        </Block>
        <Block title="Buyer (Bill to)" testid="block-buyer">
          <KV k="Name" v={invoice.customer_name || "—"} />
          <KV k="Mobile" v={`+91 ${invoice.customer_mobile}`} />
          {invoice.customer_email && <KV k="Email" v={invoice.customer_email} />}
          <KV k="State" v={`${invoice.customer_state || "—"} (${invoice.customer_state_code || "—"})`} />
          <KV k="GSTIN" v={invoice.customer_gstin || "Unregistered (B2C)"} mono={!!invoice.customer_gstin} />
          {invoice.customer_ref && <KV k="Ref" v={invoice.customer_ref} mono />}
        </Block>
      </div>

      <div className="bento-card p-5 mb-5" data-testid="block-line-items">
        <h2 className="font-display font-medium text-lg text-[#121C17] mb-4">Line items</h2>
        <table className="w-full text-sm">
          <thead className="text-[#5C6661] text-left text-xs">
            <tr className="border-b border-[#E5E2DC]">
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 font-medium">SAC</th>
              <th className="py-2 font-medium text-right">Taxable</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-3">{invoice.description}</td>
              <td className="py-3 font-mono">{invoice.sac_code}</td>
              <td className="py-3 text-right font-medium">₹{(invoice.subtotal || 0).toLocaleString("en-IN")}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mb-5">
        <div className="bento-card p-5 md:col-span-2" data-testid="block-tax-breakup">
          <h2 className="font-display font-medium text-lg text-[#121C17] mb-3">Tax breakup</h2>
          <div className="space-y-2 text-sm">
            <Row k="Taxable value" v={`₹${(invoice.subtotal || 0).toLocaleString("en-IN")}`} />
            {invoice.gst_type === "CGST_SGST" ? (
              <>
                <Row k={`CGST @ ${invoice.cgst_rate}%`} v={`₹${(invoice.cgst || 0).toLocaleString("en-IN")}`} />
                <Row k={`SGST @ ${invoice.sgst_rate}%`} v={`₹${(invoice.sgst || 0).toLocaleString("en-IN")}`} />
              </>
            ) : (
              <Row k={`IGST @ ${invoice.igst_rate}%`} v={`₹${(invoice.igst || 0).toLocaleString("en-IN")}`} />
            )}
            <div className="border-t border-[#E5E2DC] pt-2 mt-2">
              <Row k="Total tax" v={`₹${(invoice.total_tax || 0).toLocaleString("en-IN")}`} bold />
            </div>
          </div>
          <div className="mt-4 p-4 rounded-xl bg-[#1E4D3B] text-white">
            <div className="text-xs uppercase tracking-wider text-white/70">Total amount payable</div>
            <div className="font-display font-medium text-2xl mt-1">
              ₹{(invoice.total_amount || 0).toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <div className="bento-card p-5" data-testid="block-meta">
          <h2 className="font-display font-medium text-lg text-[#121C17] mb-3">Meta</h2>
          <KV k="Place of supply" v={invoice.place_of_supply} />
          <KV k="GST type" v={invoice.gst_type === "IGST" ? "Inter-state (IGST)" : "Intra-state (CGST+SGST)"} />
          <KV k="Payment method" v={invoice.payment_method || "—"} />
          {invoice.razorpay_payment_id && <KV k="Razorpay ID" v={invoice.razorpay_payment_id} mono />}

          <div className="mt-4">
            <div className="text-xs text-[#5C6661] mb-2">Status</div>
            <span className={`inline-block text-xs px-3 py-1.5 rounded-full uppercase font-medium ${STATUS_COLOR[invoice.status] || ""}`}
              data-testid="invoice-status-pill">
              {invoice.status}
            </span>
          </div>

          <div className="mt-4 space-y-2">
            <div className="text-xs text-[#5C6661]">Change status</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.filter((s) => s !== invoice.status).map((s) => (
                <button key={s} disabled={busy} onClick={() => onStatusChange(s)}
                  data-testid={`status-set-${s}`}
                  className="text-xs px-3 py-1 rounded-full border border-[#E5E2DC] hover:border-[#1E4D3B] hover:text-[#1E4D3B] disabled:opacity-60">
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Block({ title, testid, children }) {
  return (
    <div className="bento-card p-5" data-testid={testid}>
      <h2 className="font-display font-medium text-lg text-[#121C17] mb-3">{title}</h2>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-[#5C6661] min-w-[100px]">{k}</span>
      <span className={`text-[#121C17] ${mono ? "font-mono text-[12px]" : ""}`}>{v}</span>
    </div>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className="flex justify-between">
      <span className={bold ? "font-medium text-[#121C17]" : "text-[#5C6661]"}>{k}</span>
      <span className={bold ? "font-medium text-[#121C17]" : "text-[#121C17]"}>{v}</span>
    </div>
  );
}
